import { useState, useEffect, useCallback } from 'react';
import { QuestionHeader } from '@fake-stack-overflow/shared';
import { useNavigate } from 'react-router-dom';
import { fetchVectorSearchResults } from '../services/questionService';

const useVectorSearch = () => {
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<QuestionHeader[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [isFirstSearch, setIsFirstSearch] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && results.length > 0) {
      setShowResults(true);
    }
  }, [isLoading, results, setShowResults]);

  const search = useCallback(async (searchQuery: string) => {
    setIsFirstSearch(false);
    if (searchQuery.trim() === '') {
      setResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchVectorSearchResults(searchQuery);
      if ('error' in data) {
        throw data.error;
      }
      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An unknown error occurred during search'));
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    if (event.target.value.trim() !== '') {
      setShowResults(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      event.preventDefault();
      setQuery(inputValue);
      setShowResults(false);
      search(inputValue);
    }
  };

  const handleSearch = () => {
    if (inputValue.trim() !== '') {
      setQuery(inputValue);
      setShowResults(false);
      search(inputValue);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowResults(false), 200);
  };

  const handleFocus = () => {
    if (inputValue.trim() !== '' && results.length > 0) {
      setShowResults(true);
    }
  };
  const navigateToQuestion = useCallback(
    (id: string) => {
      navigate(`/question/${id}`);
    },
    [navigate],
  );

  const handleResultClick = (result: QuestionHeader) => {
    setInputValue(result.title);
    setShowResults(false);
    navigateToQuestion(String(result._id));
  };

  return {
    query,
    results,
    isLoading,
    error,
    inputValue,
    showResults,
    isFirstSearch,
    setShowResults,
    handleInputChange,
    handleKeyDown,
    handleSearch,
    handleResultClick,
    handleBlur,
    handleFocus,
  };
};

export default useVectorSearch;
